import { DrawerNoHeader } from '../../components/drawer/DrawerNoHeader'
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { getAllPaymentByUserId, getPaymentById } from '../../redux/actions/payment';
import { optionsAllAccountsBank, optionsAllCourseListOpenByIdUser } from '../../redux/actions/options';
import { AvatarWithInitials } from '../../components/avatar/AvatarWithInitials';
import { TabsPaymentValidation } from './TabsPaymentValidation';
import { Button } from '../../components/buttons/Button';

export const DrawerPaymentValidation = ({
    isOpenDrawerFilters,
    toggleDrawerFilters,
    idPayment,
    updateDataTable
}) => {
    const dispatch = useDispatch();

    const {
        paymentSelected,
        loadingSelectedPayment
    } = useSelector((state) => state.payment);

    // Estados para almacenar las funciones del tab
    const [handleValidatePayment, setHandleValidatePayment] = useState(null);
    const [handleDeclinePayment, setHandleDeclinePayment] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const [isEditingReceiptDate, setIsEditingReceiptDate] = useState(false);
    const [receiptDateSaveCallback, setReceiptDateSaveCallback] = useState(null);
    const [receiptDateCancelCallback, setReceiptDateCancelCallback] = useState(null);
    const [loadingReceiptDateSaving, setLoadingReceiptDateSaving] = useState(false);
    const [activeMenuOption, setActiveMenuOption] = useState(null);
    const [isDetailsTabActive, setIsDetailsTabActive] = useState(true);

    const isAuxPanelOpen = Boolean(activeMenuOption);

    useEffect(() => {
        if (!isOpenDrawerFilters) setActiveMenuOption(null);
    }, [isOpenDrawerFilters]);

    useEffect(() => {
        setActiveMenuOption(null);
    }, [idPayment]);

    const handleSelectMenuOption = (optionId) => {
        setActiveMenuOption((prev) => (prev === optionId ? null : optionId));
    };

    const isValidPaymentId = (id) => typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id);

    useEffect(() => {
        if (!isOpenDrawerFilters || !isValidPaymentId(idPayment)) return;
        dispatch(getPaymentById(idPayment));
    }, [dispatch, idPayment, isOpenDrawerFilters]);

    useEffect(() => {
        dispatch(optionsAllAccountsBank());
    }, [dispatch]);

    const paymentUserId = paymentSelected?.user?._id;

    useEffect(() => {
        if (!paymentUserId) return;
        dispatch(getAllPaymentByUserId(paymentUserId));
    }, [dispatch, paymentUserId]);

    useEffect(() => {
        if (!paymentUserId) return;
        dispatch(optionsAllCourseListOpenByIdUser(paymentUserId));
    }, [dispatch, paymentUserId]);

    // Funciones para recibir los handlers del tab
    const onValidatePayment = (handler) => {
        setHandleValidatePayment(() => handler);
    };

    const onDeclinePayment = (handler) => {
        setHandleDeclinePayment(() => handler);
    };

    const onLoadingChange = (loading) => {
        setIsProcessing(loading);
    };

    // Manejadores de los botones del footer
    const handleValidateClick = () => {
        if (handleValidatePayment) {
            handleValidatePayment();
        }
    };

    const handleDeclineClick = () => {
        if (handleDeclinePayment) {
            handleDeclinePayment();
        }
    };

    return (
        <DrawerNoHeader
            isOpen={isOpenDrawerFilters}
            toggleDrawer={isProcessing || loadingReceiptDateSaving ? () => {} : toggleDrawerFilters}
            direction='right'
            menuBar={{
                enabled: true,
                visible: isDetailsTabActive,
                activeId: activeMenuOption,
                onSelect: handleSelectMenuOption,
                buttons: [
                    {
                        id: 'aux-space',
                        title: isAuxPanelOpen ? 'Ocultar comprobante de pago' : 'Mostrar comprobante de pago',
                        icon: isAuxPanelOpen
                            ? <ArrowsPointingInIcon className='h-6 w-6' aria-hidden />
                            : <ArrowsPointingOutIcon className='h-6 w-6' aria-hidden />,
                    },
                ],
            }}
            menuExpand={{
                enabled: isOpenDrawerFilters,
                isExpanded: isAuxPanelOpen,
                sizeLG: '75%',
                sizeXL: '70%',
                sizeXXL: '60%',
            }}
            footer={(
                isEditingReceiptDate ? (
                    <div className='flex items-center justify-end gap-4 overflow-hidden'>
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={loadingReceiptDateSaving}
                            onClick={() => receiptDateCancelCallback && receiptDateCancelCallback()}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            loading={loadingReceiptDateSaving}
                            onClick={() => receiptDateSaveCallback && receiptDateSaveCallback()}
                        >
                            Guardar
                        </Button>
                    </div>
                ) : (
                    <div className='flex items-center justify-end gap-4'>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => toggleDrawerFilters()}
                            disabled={isProcessing}
                        >
                            Cerrar
                        </Button>
                        {(paymentSelected?.status === 'pendiente' && !loadingSelectedPayment) && (
                            <Button
                                type="button"
                                variant="reject"
                                onClick={handleDeclineClick}
                                disabled={isProcessing}
                            >
                                Rechazar
                            </Button>
                        )}
                        {(paymentSelected?.status === 'pendiente' && !loadingSelectedPayment) && (
                            <Button
                                type="button"
                                variant="primary"
                                onClick={handleValidateClick}
                                disabled={isProcessing}
                                loading={isProcessing}
                                loadingLabel="Validando..."
                                className="min-w-[100px]"
                            >
                                Validar
                            </Button>
                        )}
                    </div>
                )
            )}
        >
            <div className='w-full h-full flex flex-col overflow-hidden'>
                {/* Header con gradiente y datos del estudiante */}
                <div className='w-full bg-white rounded-lg border border-gray-200 flex-shrink-0'>
                    {/* Header con gradiente */}
                    <div className='bg-gradient-to-r from-indigo-500 to-purple-600 p-5 md:p-6 rounded-tr-lg'>
                        <div className='flex items-center gap-4 md:gap-5'>
                            {/* Avatar con iniciales */}
                            <div className='relative flex-shrink-0'>
                                <AvatarWithInitials
                                    firstName={paymentSelected.user?.firstName || ''}
                                    lastName={paymentSelected.user?.lastName || ''}
                                    loading={loadingSelectedPayment}
                                    size="md"
                                />
                            </div>

                            {/* Información principal */}
                            <div className='flex-1 min-w-0'>
                                {loadingSelectedPayment ? (
                                    <>
                                        {/* Skeleton mejorado para nombre con shimmer effect */}
                                        <div className='flex items-center gap-2 mb-3 flex-wrap'>
                                            <div className='h-7 md:h-8 w-40 md:w-64 bg-white/30 rounded-lg animate-pulse relative overflow-hidden'>
                                                <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                            </div>
                                            <div className='hidden md:block h-6 w-20 bg-white/20 rounded-full animate-pulse relative overflow-hidden'>
                                                <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                            </div>
                                        </div>
                                        {/* Skeleton para badges de info - solo desktop */}
                                        <div className='hidden md:flex flex-wrap gap-2'>
                                            <div className='h-8 w-32 bg-white/20 rounded-lg animate-pulse relative overflow-hidden'>
                                                <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                            </div>
                                            <div className='h-8 w-28 bg-white/20 rounded-lg animate-pulse relative overflow-hidden'>
                                                <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className='flex items-center gap-2 mb-3 flex-wrap'>
                                            {/* Nombre con loading */}
                                            {!paymentSelected.user?.firstName && !paymentSelected.user?.lastName ? (
                                                <div className='h-7 md:h-8 w-40 md:w-64 bg-white/30 rounded-lg animate-pulse relative overflow-hidden'>
                                                    <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                                </div>
                                            ) : (
                                                <h3 className='text-white font-bold text-lg md:text-xl lg:text-2xl capitalize tracking-tight'>
                                                    {`${paymentSelected.user?.firstName || ''} ${paymentSelected.user?.secondName || ''} ${paymentSelected.user?.lastName || ''} ${paymentSelected.user?.secondSurname || ''}`.trim() || 'Usuario'}
                                                </h3>
                                            )}
                                            
                                        </div>

                                        {/* Info badges - solo en desktop */}
                                        <div className='flex flex-wrap gap-2'>
                                            {/* Badge de matrícula con loading */}
                                            {!paymentSelected.user?.matricula ? (
                                                <div className='hidden lg:block h-8 w-32 bg-white/20 rounded-lg animate-pulse relative overflow-hidden'>
                                                    <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                                </div>
                                            ) : (
                                                <div className='hidden lg:inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/30'>
                                                    <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                                                    </svg>
                                                    <span className='text-white text-sm font-semibold'>
                                                        {paymentSelected.user.matricula}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contenedor de tabs con overflow controlado */}
                <div className='flex-1 overflow-hidden'>
                    <TabsPaymentValidation
                        isOpen={isOpenDrawerFilters}
                        idPayment={idPayment}
                        toggleDrawerFilters={toggleDrawerFilters}
                        updateDataTable={updateDataTable}
                        onValidatePayment={onValidatePayment}
                        onDeclinePayment={onDeclinePayment}
                        onLoadingChange={onLoadingChange}
                        onReceiptEditModeChange={setIsEditingReceiptDate}
                        onReceiptDateSaveCallbackChange={setReceiptDateSaveCallback}
                        onReceiptDateCancelCallbackChange={setReceiptDateCancelCallback}
                        onReceiptDateLoadingSavingChange={setLoadingReceiptDateSaving}
                        loadingReceiptDateSaving={loadingReceiptDateSaving}
                        isReceiptAuxOpen={isAuxPanelOpen}
                        onClearReceiptAux={() => setActiveMenuOption(null)}
                        onDetailsTabActiveChange={setIsDetailsTabActive}
                    />
                </div>
            </div>
        </DrawerNoHeader>
    )
}
